'use client';

import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react';
import {
  Cog6ToothIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  EyeIcon,
  ClockIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { 
  Bars3Icon, 
  ChevronRightIcon, 
  ChevronLeftIcon
} from '@heroicons/react/20/solid';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiSpeedometer, GiEchoRipples } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import BreachDetailsDrawer from './BreachDetailsDrawer';
import { Link, useLocation } from 'react-router-dom';
import usePageTitle from '../utils/usePageTitle';

// Function to filter navigation items based on current path
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Monitor() {
  usePageTitle('Security Monitor');

  const [identifiers, setIdentifiers] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedBreach, setSelectedBreach] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingBreaches, setLoadingBreaches] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('breached');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  
  const location = useLocation();
  const navigation = getFilteredNavigation(location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Function to sort identifiers based on criteria
  const sortIdentifiers = (ids, criterion) => {
    return [...ids].sort((a, b) => {
      switch (criterion) {
        case 'breached':
          if (a.hasBreaches && !b.hasBreaches) return -1;
          if (!a.hasBreaches && b.hasBreaches) return 1;
          return a.identifier.localeCompare(b.identifier);
        case 'name':
          return a.identifier.localeCompare(b.identifier);
        case 'date':
          if (a.lastScannedAt === '—' && b.lastScannedAt !== '—') return 1;
          if (a.lastScannedAt !== '—' && b.lastScannedAt === '—') return -1;
          if (a.lastScannedAt === '—' && b.lastScannedAt === '—') return 0;
          const dateA = a.lastScannedAt.split(' UTC')[0];
          const dateB = b.lastScannedAt.split(' UTC')[0];
          return new Date(dateB) - new Date(dateA);
        case 'status':
          if (a.hasBreaches !== b.hasBreaches) {
            return a.hasBreaches ? -1 : 1;
          }
          return a.identifier.localeCompare(b.identifier);
        default:
          return 0;
      }
    });
  };

  // Filter identifiers based on search term
  const filteredIdentifiers = identifiers.filter(identifier =>
    identifier.identifier.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fetch identifiers once on mount
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user || !isMounted) {
        if (isMounted) setLoading(false);
        return;
      }
      
      try {
        const identifiersQuery = query(collection(db, 'userMonitor', user.uid, 'identifiers'));
        const identifiersSnap = await getDocs(identifiersQuery);
        
        if (!isMounted) return;
        
        const ids = identifiersSnap.docs.map(doc => {
          const d = doc.data();
          return {
            id: doc.id,
            identifier: d.identifier || doc.id,
            hasBreaches: !!d.hasBreaches,
            lastScannedAt: d.lastScannedAt
              ? d.lastScannedAt.toDate().toLocaleString('en-US', {
                  timeZone: 'America/New_York',
                  month: 'long', day: 'numeric', year: 'numeric',
                  hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true,
                }) + ' UTC-4'
              : '—',
          };
        });
        
        const sortedIds = sortIdentifiers(ids, sortBy);
        if (isMounted) {
          setIdentifiers(sortedIds);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching identifiers:", err);
        if (isMounted) {
          setError({
            message: "Unable to access breach data. Please ensure you have the proper permissions.",
            details: err.message
          });
          setLoading(false);
        }
      }
    });
    
    return () => {
      isMounted = false;
      unsub();
    };
  }, [sortBy]);

  // Handle sorting change
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    if (identifiers.length > 0) {
      setIdentifiers(sortIdentifiers(identifiers, newSortBy));
    }
  };

  // Fetch breaches when an identifier is selected
  useEffect(() => {
    if (!selectedId) {
      setBreaches([]);
      return;
    }
    
    let isMounted = true;
    setLoadingBreaches(true);
    
    (async () => {
      const user = auth.currentUser;
      if (!user || !isMounted) {
        if (isMounted) setLoadingBreaches(false);
        return;
      }
      
      try {
        const breachesQuery = query(
          collection(db, 'userMonitor', user.uid, 'breaches'),
          where('identifier', '==', selectedId.identifier)
        );
        const breachesSnap = await getDocs(breachesQuery);
        
        if (!isMounted) return;
        
        setBreaches(breachesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoadingBreaches(false);
      } catch (err) {
        console.error("Error fetching breaches:", err);
        if (isMounted) {
          setLoadingBreaches(false);
        }
      }
    })();
    
    return () => {
      isMounted = false;
    };
  }, [selectedId]);

  const handleSelectIdentifier = (identifier) => {
    setSelectedId(identifier);
    if (isMobileView) {
      // On mobile, we might want to navigate to a different view or show modal
    }
  };

  const handleBackToList = () => {
    setSelectedId(null);
  };

  return (
    <>
      {/* Mobile sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
        <div className="fixed inset-0 flex">
          <DialogPanel transition className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="flex h-16 shrink-0 items-center justify-center px-4">
                <img alt="Relay" src="/images/logowhite.png" className="h-8 w-auto" />
              </div>
              <nav className="flex flex-1 flex-col px-4">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="space-y-3">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200 hover:bg-white/5 rounded-lg"
                            onClick={() => setSidebarOpen(false)}
                          >
                            <item.icon 
                              aria-hidden="true" 
                              className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )} 
                            />
                            <span className={classNames(
                              "transition-colors duration-200",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )}>
                              {item.name}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto mb-4">
                    <Link
                      to={settingsNavItem.href}
                      className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200 hover:bg-white/5 rounded-lg"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      <span className={classNames(
                        "transition-colors duration-200",
                        settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                      )}>
                        {settingsNavItem.name}
                      </span>
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
        sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
      }`}>
        <div className="relative">
          <div 
            className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
            style={{
              background: 'rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(20px)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
              height: 'calc(100vh - 2rem)'
            }}
          >
            <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
              <img
                alt="Relay"
                src="/images/logowhite.png"
                className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
              />
            </div>

            <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="space-y-3">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={classNames(
                            'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg hover:bg-white/5',
                            sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                          )}
                          title={!sidebarExpanded ? item.name : undefined}
                        >
                          <item.icon 
                            aria-hidden="true" 
                            className={classNames(
                              "h-6 w-6 shrink-0 transition-colors duration-200",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )} 
                          />
                          {sidebarExpanded && (
                            <span className={classNames(
                              "transition-opacity duration-300",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )}>
                              {item.name}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="mt-auto mb-4">
                  <Link
                    to={settingsNavItem.href}
                    className={classNames(
                      'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg hover:bg-white/5',
                      sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                    )}
                    title={!sidebarExpanded ? settingsNavItem.name : undefined}
                  >
                    <settingsNavItem.icon 
                      aria-hidden="true" 
                      className={classNames(
                        "h-6 w-6 shrink-0 transition-colors duration-200",
                        settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                      )} 
                    />
                    {sidebarExpanded && (
                      <span className={classNames(
                        "transition-opacity duration-300",
                        settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                      )}>
                        {settingsNavItem.name}
                      </span>
                    )}
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Right border expand/collapse area */}
          <div 
            className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            style={{ zIndex: 60 }}
          >
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
              {sidebarExpanded ? (
                <ChevronLeftIcon className="h-4 w-4 text-white/70" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-white/70" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div 
        className={`flex-1 min-h-screen transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`}
        style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}
      >
        {/* Mobile header */}
        <button 
          type="button" 
          onClick={() => setSidebarOpen(true)} 
          className="fixed top-4 left-4 z-50 lg:hidden bg-white/10 backdrop-blur-md rounded-full p-3 text-white hover:bg-white/20 transition-all border border-white/20"
          style={{
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
          }}
        >
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>

        <div className="px-4 py-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl font-light text-white mb-2">
                Security Monitor
              </h1>
              <p className="text-xl text-white/60">
                Track and monitor your digital footprint for data breaches
              </p>
            </div>
          </div>

          {/* Stats Overview - Mobile and Desktop */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                 style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
              <div className="flex items-center gap-x-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-500/20 rounded-lg">
                  <EyeIcon className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="text-lg font-light text-white">Monitored</h3>
              </div>
              <div className="text-3xl font-light text-blue-400">
                {loading ? '—' : identifiers.length}
              </div>
              <div className="text-white/60 text-sm">Total Identifiers</div>
            </div>

            <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                 style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
              <div className="flex items-center gap-x-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-red-500/20 rounded-lg">
                  <ShieldExclamationIcon className="h-5 w-5 text-red-400" />
                </div>
                <h3 className="text-lg font-light text-white">Breached</h3>
              </div>
              <div className="text-3xl font-light text-red-400">
                {loading ? '—' : identifiers.filter(id => id.hasBreaches).length}
              </div>
              <div className="text-white/60 text-sm">Compromised Identifiers</div>
            </div>

            <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                 style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
              <div className="flex items-center gap-x-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-green-500/20 rounded-lg">
                  <ShieldCheckIcon className="h-5 w-5 text-green-400" />
                </div>
                <h3 className="text-lg font-light text-white">Secure</h3>
              </div>
              <div className="text-3xl font-light text-green-400">
                {loading ? '—' : identifiers.filter(id => !id.hasBreaches).length}
              </div>
              <div className="text-white/60 text-sm">Safe Identifiers</div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className={`grid gap-8 ${selectedId && !isMobileView ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
            {/* Identifiers List */}
            <div className={`${selectedId && isMobileView ? 'hidden' : ''}`}>
              <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 overflow-hidden"
                   style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                
                {/* Header with Search and Sort */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="text-xl font-light text-white">Your Identifiers</h2>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                                             {/* Search */}
                       <div className="relative flex items-center">
                         <MagnifyingGlassIcon className="absolute left-3 h-4 w-4 text-white/40 pointer-events-none z-10" />
                         <input
                           type="text"
                           placeholder="Search identifiers..."
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           className="block w-full sm:w-64 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm pl-10 pr-3 py-2 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10 text-sm transition-all duration-200"
                         />
                       </div>
                      
                      {/* Sort Menu */}
                      <Menu as="div" className="relative">
                        <MenuButton className="inline-flex items-center gap-x-2 rounded-xl bg-white/5 backdrop-blur-sm px-4 py-2 text-sm font-medium text-white border border-white/10 hover:bg-white/10 transition-all duration-200">
                          Sort by
                          <ChevronDownIcon className="h-4 w-4" />
                        </MenuButton>
                        <MenuItems className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-xl bg-black/20 backdrop-blur-2xl border border-white/10 py-2 shadow-2xl ring-1 ring-white/5 focus:outline-none"
                                   style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'}}>
                          {[
                            { key: 'breached', label: 'Breached First' },
                            { key: 'name', label: 'Name' },
                            { key: 'date', label: 'Last Scanned' },
                            { key: 'status', label: 'Status' },
                          ].map((option) => (
                            <MenuItem key={option.key}>
                              <button
                                onClick={() => handleSortChange(option.key)}
                                className={classNames(
                                  'block w-full text-left px-3 py-2 text-sm transition-colors duration-200',
                                  sortBy === option.key
                                    ? 'text-white bg-white/10'
                                    : 'text-white/80 hover:bg-white/5 hover:text-white'
                                )}
                              >
                                {option.label}
                              </button>
                            </MenuItem>
                          ))}
                        </MenuItems>
                      </Menu>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="animate-pulse flex items-center justify-between p-4 bg-white/5 rounded-xl">
                          <div className="flex items-center space-x-4">
                            <div className="w-3 h-3 bg-white/10 rounded-full"></div>
                            <div>
                              <div className="h-4 bg-white/10 rounded w-32 mb-2"></div>
                              <div className="h-3 bg-white/10 rounded w-24"></div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="h-6 bg-white/10 rounded-full w-16"></div>
                            <div className="w-5 h-5 bg-white/10 rounded"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">Access Error</h3>
                      <p className="text-white/60 mb-4">{error.message}</p>
                      <Link 
                        to="/dashboard" 
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors duration-200"
                      >
                        Return to Dashboard
                      </Link>
                    </div>
                  ) : filteredIdentifiers.length === 0 ? (
                    <div className="text-center py-12">
                      <GiRadarSweep className="h-12 w-12 text-white/40 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">
                        {searchTerm ? 'No Results Found' : 'No Identifiers Yet'}
                      </h3>
                      <p className="text-white/60">
                        {searchTerm 
                          ? `No identifiers match "${searchTerm}"`
                          : 'Your monitored identifiers will appear here once available.'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredIdentifiers.map((identifier) => (
                        <div
                          key={identifier.id}
                          onClick={() => handleSelectIdentifier(identifier)}
                          className={classNames(
                            'flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all duration-200',
                            selectedId?.id === identifier.id
                              ? 'bg-white/10 border border-white/20'
                              : 'bg-white/5 hover:bg-white/10 border border-transparent'
                          )}
                        >
                          <div className="flex items-center space-x-4">
                            <div className={classNames(
                              'flex-none rounded-full p-1',
                              identifier.hasBreaches 
                                ? 'text-red-400 bg-red-400/20'
                                : 'text-green-400 bg-green-400/20'
                            )}>
                              <div className="h-3 w-3 rounded-full bg-current" />
                            </div>
                            <div>
                              <h3 className="text-white font-medium text-sm">
                                {identifier.identifier}
                              </h3>
                              <div className="flex items-center gap-x-2 mt-1">
                                <ClockIcon className="h-3 w-3 text-white/40" />
                                <p className="text-white/60 text-xs">
                                  {identifier.lastScannedAt}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={classNames(
                              'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset',
                              identifier.hasBreaches
                                ? 'text-red-400 bg-red-400/10 ring-red-400/30'
                                : 'text-green-400 bg-green-400/10 ring-green-400/30'
                            )}>
                              {identifier.hasBreaches ? 'Breached' : 'Secure'}
                            </span>
                            <ChevronRightIcon className="h-5 w-5 text-white/40" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Breach Details Panel */}
            {selectedId && (
              <div className={`${isMobileView ? 'absolute inset-0 z-40' : ''}`}>
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 overflow-hidden h-full"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                  
                  {/* Header */}
                  <div className="p-6 border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-light text-white">Breach Details</h2>
                        <p className="text-white/60 text-sm mt-1">{selectedId.identifier}</p>
                      </div>
                      {isMobileView && (
                        <button
                          onClick={handleBackToList}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
                        >
                          <XMarkIcon className="h-6 w-6 text-white/60" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {loadingBreaches ? (
                      <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="animate-pulse p-4 bg-white/5 rounded-xl">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="w-8 h-8 bg-white/10 rounded-full"></div>
                              <div className="h-4 bg-white/10 rounded w-32"></div>
                            </div>
                            <div className="h-3 bg-white/10 rounded w-full mb-1"></div>
                            <div className="h-3 bg-white/10 rounded w-24"></div>
                          </div>
                        ))}
                      </div>
                    ) : breaches.length === 0 ? (
                      <div className="text-center py-12">
                        <ShieldCheckIcon className="h-12 w-12 text-green-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-white mb-2">All Clear!</h3>
                        <p className="text-white/60">
                          No breaches found for this identifier.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {breaches.map((breach) => (
                          <div
                            key={breach.id}
                            onClick={() => setSelectedBreach(breach)}
                            className="p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors duration-200"
                          >
                            <div className="flex items-start space-x-3">
                              {breach.LogoPath && (
                                <img 
                                  alt="" 
                                  src={breach.LogoPath} 
                                  className="h-8 w-8 rounded-full bg-gray-800 flex-shrink-0" 
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-white font-medium text-sm">
                                  {breach.Title}
                                </h3>
                                {breach.Domain && (
                                  <p className="text-white/60 text-xs mt-1">
                                    {breach.Domain}
                                  </p>
                                )}
                                {breach.BreachDate && (
                                  <p className="text-white/40 text-xs mt-1">
                                    {breach.BreachDate}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Breach Details Drawer */}
      {selectedBreach && (
        <BreachDetailsDrawer
          breach={selectedBreach}
          isOpen={!!selectedBreach}
          onClose={() => setSelectedBreach(null)}
        />
      )}
    </>
  );
}
